import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Helmet } from "react-helmet";

const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Politique de confidentialité | GoBledPro</title>
        <meta name="description" content="Politique de confidentialité de GoBledPro. Découvrez comment nous collectons, utilisons et protégeons vos données personnelles." />
        <link rel="canonical" href="https://gobledpro.com/confidentialite" />
      </Helmet>
      <Header />
      <main className="pt-20 section-padding">
        <div className="container mx-auto max-w-3xl">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8">
            Politique de confidentialité
          </h1>

          <div className="prose prose-lg max-w-none text-muted-foreground space-y-8">
            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                1. Introduction
              </h2>
              <p>
                La présente politique de confidentialité décrit la manière dont 
                <strong className="text-foreground"> GoBledPro</strong> collecte, utilise et protège 
                les informations personnelles que vous nous fournissez lors de l'utilisation 
                de notre plateforme de mise en relation pour le transport de colis entre la France 
                (et l'Europe) et le Maroc.
              </p>
              <p>
                En utilisant notre site, vous acceptez les pratiques décrites dans cette politique.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                2. Responsable du traitement
              </h2>
              <p>
                Le responsable du traitement des données personnelles est :
              </p>
              <p className="mt-2">
                <strong className="text-foreground">GoBledPro</strong><br />
                Plateforme éditée par son porteur de projet<br />
                Email : gobledpro@gmail.com<br />
                Téléphone : +33 9 55 44 39 02
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                3. Données collectées
              </h2>
              <p>
                Nous collectons les données personnelles suivantes :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-foreground">Données d'identification :</strong> nom, prénom, adresse email, numéro de téléphone</li>
                <li><strong className="text-foreground">Données de transport :</strong> villes de départ et d'arrivée, type de colis, dimensions, poids, date souhaitée</li>
                <li><strong className="text-foreground">Données de connexion :</strong> adresse IP, type de navigateur, pages consultées, date et heure de connexion</li>
                <li><strong className="text-foreground">Pour les transporteurs :</strong> informations professionnelles (SIRET, zones de couverture, type de véhicule, assurance)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                4. Finalités du traitement
              </h2>
              <p>
                Vos données personnelles sont collectées pour les finalités suivantes :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Gestion des demandes de transport et mise en relation avec les transporteurs</li>
                <li>Création et gestion de votre compte utilisateur</li>
                <li>Communication relative à vos demandes (notifications, suivi)</li>
                <li>Amélioration de nos services et de l'expérience utilisateur</li>
                <li>Respect de nos obligations légales et réglementaires</li>
                <li>Gestion des candidatures transporteurs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                5. Base légale du traitement
              </h2>
              <p>
                Le traitement de vos données repose sur :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-foreground">L'exécution du contrat :</strong> traitement des demandes de transport</li>
                <li><strong className="text-foreground">Le consentement :</strong> envoi de communications informatives ou marketing (le cas échéant)</li>
                <li><strong className="text-foreground">L'intérêt légitime :</strong> amélioration du service, sécurité de la plateforme</li>
                <li><strong className="text-foreground">L'obligation légale :</strong> respect des obligations réglementaires</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                6. Durée de conservation
              </h2>
              <p>
                Les données personnelles sont conservées pour une durée proportionnée aux finalités poursuivies :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-foreground">Données de compte :</strong> durée de l'inscription, puis 3 ans après la dernière activité</li>
                <li><strong className="text-foreground">Demandes de transport :</strong> 5 ans à compter de la fin de la prestation</li>
                <li><strong className="text-foreground">Candidatures transporteurs :</strong>
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>2 ans en cas de refus</li>
                    <li>Durée du partenariat en cas d'acceptation</li>
                  </ul>
                </li>
                <li><strong className="text-foreground">Données de connexion :</strong> 1 an</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                7. Destinataires des données
              </h2>
              <p>
                Vos données peuvent être communiquées à :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-foreground">Transporteurs partenaires :</strong> uniquement les informations nécessaires à la réalisation du transport</li>
                <li><strong className="text-foreground">Prestataires techniques :</strong> hébergement, outils d'emailing, services techniques (dans le respect du RGPD)</li>
                <li><strong className="text-foreground">Autorités compétentes :</strong> en cas d'obligation légale</li>
              </ul>
              <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-4 my-4">
                <p className="text-secondary font-medium">
                  ⚠️ GoBledPro ne vend jamais vos données personnelles à des tiers.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                8. Transferts hors Union Européenne
              </h2>
              <p>
                Dans le cadre des transports vers le Maroc, certaines données peuvent être transférées 
                hors de l'Union Européenne. Ces transferts sont encadrés par des garanties appropriées 
                (clauses contractuelles types ou mécanismes reconnus) afin d'assurer un niveau de 
                protection équivalent à celui exigé par le RGPD.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                9. Sécurité des données
              </h2>
              <p>
                GoBledPro met en œuvre des mesures techniques et organisationnelles appropriées 
                pour protéger vos données personnelles contre tout accès non autorisé, perte, 
                altération ou divulgation, notamment :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Chiffrement des données sensibles</li>
                <li>Accès restreint aux données personnelles</li>
                <li>Sauvegardes régulières</li>
                <li>Protocoles de sécurité (HTTPS)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                10. Vos droits
              </h2>
              <p>
                Conformément au Règlement Général sur la Protection des Données (RGPD), 
                vous disposez des droits suivants :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Droit d'accès</li>
                <li>Droit de rectification</li>
                <li>Droit à l'effacement</li>
                <li>Droit à la limitation du traitement</li>
                <li>Droit à la portabilité des données</li>
                <li>Droit d'opposition</li>
              </ul>
              <p className="mt-4">
                Pour exercer vos droits, vous pouvez nous contacter à : <strong className="text-foreground">gobledpro@gmail.com</strong>
              </p>
              <p>
                Vous disposez également du droit d'introduire une réclamation auprès de la CNIL : {" "}
                <a href="https://www.cnil.fr" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                11. Cookies
              </h2>
              <p>
                Le site GoBledPro utilise des cookies :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-foreground">Cookies essentiels :</strong> nécessaires au fonctionnement du site (sécurité, navigation)</li>
                <li><strong className="text-foreground">Cookies analytiques :</strong> permettant de mesurer l'audience et améliorer le service</li>
              </ul>
              <p className="mt-4">
                Les cookies analytiques sont déposés uniquement après obtention de votre consentement.
                La durée de conservation des cookies est limitée à 13 mois maximum.
              </p>
              <p className="mt-2">
                Vous pouvez configurer votre navigateur pour refuser les cookies non essentiels.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                12. Mineurs
              </h2>
              <p>
                Le service GoBledPro est destiné aux personnes majeures.
                Nous ne collectons pas sciemment de données personnelles concernant des personnes 
                de moins de 18 ans.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                13. Délégué à la protection des données
              </h2>
              <p>
                GoBledPro n'a pas désigné de Délégué à la Protection des Données (DPO).
                Pour toute question relative aux données personnelles, vous pouvez nous contacter 
                à l'adresse indiquée ci-dessus.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                14. Modifications de la politique
              </h2>
              <p>
                GoBledPro se réserve le droit de modifier la présente politique de confidentialité 
                à tout moment. Les modifications prennent effet dès leur publication sur cette page.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                15. Contact
              </h2>
              <p>
                Pour toute question relative à cette politique de confidentialité ou à l'exercice de vos droits :
              </p>
              <p className="mt-2">
                📧 <strong className="text-foreground">gobledpro@gmail.com</strong><br />
                📞 <strong className="text-foreground">+33 9 55 44 39 02</strong>
              </p>
            </section>

            <p className="text-sm text-muted-foreground pt-8 border-t border-border">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PolitiqueConfidentialite;
